/**
 * WEB-3956 юнит 8 — публичный раздел маршрутов: приглашение в команду и
 * публичный плеер на апексе (`PUBLIC_ROUTES`, в конце роутера апекса) и
 * роутер своего домена клиента (`CUSTOM_DOMAIN_ROUTES`). Собирает
 * `router/index.tsx`.
 */
import type { RouteObject } from 'react-router-dom';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { SuspenseWrap } from '../suspense';

const PublicQuizPage = lazyWithRetry(
  () => import('@/pages/PublicQuiz'),
  'page:PublicQuiz',
);
const PublicPaywallPage = lazyWithRetry(
  () => import('@/pages/PublicPaywall'),
  'page:public-paywall',
);
const PublicHostRouter = lazyWithRetry(
  () => import('@/pages/PublicHostRouter'),
  'page:PublicHostRouter',
);
// Ключ тот же, что у `PublicSystemPageLazy`: один кусок системной страницы.
const PublicSystemPage = lazyWithRetry(
  () =>
    import('@/components/public/PublicSystemPage').then((m) => ({
      default: m.PublicSystemPage,
    })),
  'page:public-system',
);
const AcceptInvitePage = lazyWithRetry(
  () => import('@/pages/AcceptInvite'),
  'page:AcceptInvite',
);

// On a user-owned domain (mybrand.com etc.), bypass the apex routing entirely
// and let PublicHostRouter resolve host+path → quiz/paywall via /public/resolve.
// Any path on a custom domain falls through to the resolver.
//
// WEB-2187: прежнее изъятие для `/handoff/*` снято — эти адреса обслуживает
// публичный вход сборки (`router/publicRouter.tsx`).
export const CUSTOM_DOMAIN_ROUTES: RouteObject[] = [
  {
    path: '*',
    element: (
      <SuspenseWrap>
        <PublicHostRouter />
      </SuspenseWrap>
    ),
  },
];

export const PUBLIC_ROUTES: RouteObject[] = [
  // Public invite accept (outside protected layout)
  {
    path: '/invite/:token',
    element: <SuspenseWrap><AcceptInvitePage /></SuspenseWrap>,
  },

  // WEB-2187: маршрутов `/handoff/*` здесь больше нет — возврат покупателя
  // после оплаты обслуживает публичный вход сборки (`router/publicRouter.tsx`).

  // Public routes (no auth required)
  //
  // Atom B (WEB slug->projectId): the public renderer resolves by-id — the
  // project's slug is no longer part of the public/preview contract. The
  // path param is now `:projectId`, used on:
  //   1. Editor preview / dev URLs — `<host>/<projectId>/<quizSlug>`.
  //   2. Legacy path mode on localhost, *.up.railway.app, and any host that
  //      is not a subdomain of the public apex.
  // On the public apex host / its subdomains and on user-owned custom
  // domains, the id comes from routeOverride (PublicHostRouter, resolved
  // server-side via /public/resolve) instead of this path param.
  //
  // React Router scores literal-segment routes (`/login`, `/quizzes`, ...)
  // higher than dynamic single-segment routes (`/:quizSlug`), so the new
  // catch-alls below only fire when no specific route claims the path.
  {
    path: '/paywall/:paywallSlug',
    element: <SuspenseWrap><PublicPaywallPage /></SuspenseWrap>,
  },
  {
    path: '/:projectId/paywall/:paywallSlug',
    element: <SuspenseWrap><PublicPaywallPage /></SuspenseWrap>,
  },
  {
    path: '/:quizSlug',
    element: <SuspenseWrap><PublicQuizPage /></SuspenseWrap>,
  },
  {
    path: '/:projectId/:quizSlug',
    element: <SuspenseWrap><PublicQuizPage /></SuspenseWrap>,
  },

  // WEB-3736 (ретро-сверка С11): адрес из трёх и более частей, которого нет,
  // не ловил ни один маршрут — на бою голая английская заглушка библиотеки
  // маршрутов. Запасной маршрут — последним, ниже всех перехватчиков:
  // системная страница «Страница не найдена» на языке браузера, как у плеера.
  {
    path: '*',
    element: (
      <SuspenseWrap>
        <PublicSystemPage kind="page" data-testid="cabinet-not-found" />
      </SuspenseWrap>
    ),
  },
];
